import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import javascript from 'react-syntax-highlighter/dist/cjs/languages/prism/javascript';
import python from 'react-syntax-highlighter/dist/cjs/languages/prism/python';
import markdown from 'react-syntax-highlighter/dist/cjs/languages/prism/markdown';
import { oneDark, dracula, vscDarkPlus, oneLight } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { formatMessageTimestamp } from '../utils/dateTime';
import { formatFileSize } from './FileAttachments';
import ImagePreview from './ImagePreview';

SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('markdown', markdown);

const Message = ({ message, image, primary, setShowReport, handleMessageMenu, messageStreaming, index, messagedAt, conversationId, messages, submitFeedback, feedbackState, onFeedbackStateChange }) => {

  const { t } = useTranslation();

  const language = localStorage.getItem('language') || 'en';

  let citations = [
    {
      start: 2,
      end: 8,
      document_ids: [
        "web-search_1",
        "web-search_8"
      ]
    },
    {
      start: 10,
      end: 12,
      document_ids: [
        "web-search_1"
      ]
    },
    {
      start: 15,
      end: 22,
      document_ids: [
        "web-search_1",
        "web-search_8"
      ]
    }
  ];

  const [openSources, setOpenSources] = useState(false);
  const [previewedImage, setPreviewedImage] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredFeedbackButton, setHoveredFeedbackButton] = useState(null);
  const feedbackAbortControllerRef = useRef(null);
  const latestFeedbackRequestRef = useRef(0);

  const messageFeedbackKey = message?.id || `feedback-${index}`;

  useEffect(() => {
    return () => {
      feedbackAbortControllerRef.current?.abort();

      message?.attachments?.forEach((attachment) => {
        if (attachment?.isLocalPreview && attachment?.previewUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(attachment.previewUrl);
        }
      });
    };
  }, [message?.attachments]);

  const handleSources = () => {
    setOpenSources(!openSources)
  }

  const handleReportBtn = () => {
    setShowReport(true);
  }

  const findPreviousUserMessage = () => {
    if (!messages || !Array.isArray(messages) || index == null) return '';
    for (let i = index - 1; i >= 0; i--) {
      if (messages[i]?.role === 'user') {
        return messages[i]?.content || '';
      }
    }
    return '';
  };

  const handleFeedback = async (feedbackType) => {
    if (feedbackState === feedbackType || message.role === 'user' || !submitFeedback) return;

    const previousFeedbackState = feedbackState || null;
    const abortController = new AbortController();
    const requestId = latestFeedbackRequestRef.current + 1;

    feedbackAbortControllerRef.current?.abort();
    feedbackAbortControllerRef.current = abortController;
    latestFeedbackRequestRef.current = requestId;

    onFeedbackStateChange?.(messageFeedbackKey, feedbackType);
    try {
      await submitFeedback({
        feedbackType,
        userMessage: findPreviousUserMessage(),
        assistantResponse: message?.content || '',
        assistantMessageId: message?.id || '',
        conversationId,
        signal: abortController.signal,
      });
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }

      if (latestFeedbackRequestRef.current === requestId) {
        onFeedbackStateChange?.(messageFeedbackKey, previousFeedbackState);
      }

      console.error('Failed to submit feedback:', error);
    } finally {
      if (latestFeedbackRequestRef.current === requestId) {
        feedbackAbortControllerRef.current = null;
      }
    }
  };

  const FeedbackButtons = () => (
    <div className={`flex items-center gap-0.5 mt-1 ml-1 opacity-100 pointer-events-auto transition-opacity duration-200 ${isHovered ? 'sm:opacity-100 sm:pointer-events-auto' : 'sm:!opacity-0 sm:!pointer-events-none'}`}>
      <button
        type="button"
        onClick={() => handleFeedback('like')}
        onMouseEnter={() => setHoveredFeedbackButton('like')}
        onMouseLeave={() => setHoveredFeedbackButton(null)}
        className={`relative flex h-7 w-7 items-center justify-center rounded-md border-none cursor-pointer transition-colors ${
          feedbackState === 'like'
            ? 'bg-green-600/10 text-green-600'
            : 'bg-transparent color-primary'
        }`}
        aria-label={t('Like response')}
      >
        {feedbackState !== 'like' && (
          <span
            className={`background-primary pointer-events-none absolute inset-0 rounded-md transition-opacity duration-200 ${hoveredFeedbackButton === 'like' ? 'opacity-0 sm:!opacity-10' : 'opacity-0'}`}
          />
        )}
        <svg className="relative z-[1]" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3z" />
          <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
        </svg>
      </button>
      <button
        type="button"
        onClick={() => handleFeedback('dislike')}
        onMouseEnter={() => setHoveredFeedbackButton('dislike')}
        onMouseLeave={() => setHoveredFeedbackButton(null)}
        className={`relative flex h-7 w-7 items-center justify-center rounded-md border-none cursor-pointer transition-colors ${
          feedbackState === 'dislike'
            ? 'bg-red-600/10 text-red-600'
            : 'bg-transparent color-primary'
        }`}
        aria-label={t('Dislike response')}
      >
        {feedbackState !== 'dislike' && (
          <span
            className={`background-primary pointer-events-none absolute inset-0 rounded-md transition-opacity duration-200 ${hoveredFeedbackButton === 'dislike' ? 'opacity-0 sm:!opacity-10' : 'opacity-0'}`}
          />
        )}
        <svg className="relative z-[1]" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3z" />
          <path d="M17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3" />
        </svg>
      </button>
    </div>
  );

  const textColor = message.role === 'user' ? 'text-white' : 'dark:text-white text-gray-600';
  const bgColor = message.role === 'user' ? `bg-[${primary}]` : 'dark:!bg-[#232326] bg-[#f5f5f5] border dark:!border-[#232326] border-[#f5f5f5]';
  const roundedClass = message.role === 'user' ? 'rounded-br-none' : 'rounded-bl-none';

  const syntaxTheme = oneDark;

  const sanitizeContent = (content) => {
    return content
      ?.replace(/</g, '&lt;')
      ?.replace(/>/g, '&gt;');
  };

  const MarkdownComponents = {
    code({ node, inline, className, children, ...props }) {
      const hasLang = /language-(\w+)/.exec(className || '');

      return hasLang ? (
        <SyntaxHighlighter
          style={syntaxTheme}
          language={hasLang[1]}
          PreTag="div"
          className="codeStyle"
          showLineNumbers={true}
          useInlineStyles={true}
          children={children}
        />
      ) : (
        <code className={className} {...props} children={children} />
      )
    },
    a({ node, ...props }) {
      return <a target="_blank" {...props} />;
    },
    table({ node, ...props }) {
      return (
        <div className='table-responsive'>
          <table className="table table-bordered" {...props} />
        </div>
      );
    },
    img({ node, ...props }) {
      return (
        <div className="img-container">
          <img 
            {...props} 
            className="cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => {
              if (props.src) {
                setPreviewedImage({ src: props.src, alt: props.alt || 'Image' });
              }
            }}
          />
        </div>
      );
    }
  }

  // function generateMarkdownWithHighlights(text) {
  //   console.log("text",text);
  //   const highlightedText = `<span class="hi">${text}</span>`;

  //   return text;
  // }

  // Transform message.files to match attachment format
  const transformedFiles = message?.files?.map((file) => {
    const fileType = file.type || '';
    // Check if file type indicates an image (e.g., "file/image", "image/png", "image/jpeg", etc.)
    // Also check common image extensions as fallback
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
    const fileExtension = file.name?.split('.').pop()?.toLowerCase() || '';
    const isImage = fileType.includes('image/') || 
                    fileType.includes('file/image') || 
                    imageExtensions.includes(fileExtension);
    const extension = file.name?.split('.').pop()?.toUpperCase() || '';
    
    return {
      id: file._id || `file-${file.name}-${Date.now()}`,
      name: file.name,
      size: file.bytes,
      readableSize: formatFileSize(file.bytes),
      type: fileType,
      isImage,
      previewUrl: isImage ? file.url : null,
      url: file.url, // Keep original URL for non-images
      extension,
      isFromFiles: true, // Flag to identify files from message.files
    };
  }) || [];

  // Merge message.attachments and transformed files
  const allAttachments = [...(message?.attachments || []), ...transformedFiles];

  const hasAttachments = allAttachments.length > 0;
  const hasTextContent = Boolean(message?.content?.trim?.());

  // Separate images from other file types
  const imageAttachments = allAttachments.filter(att => att.isImage) || [];
  const nonImageAttachments = allAttachments.filter(att => !att.isImage) || [];

  const renderMetaRow = () => (
    <div className={`w-full flex pt-1 ${message.role === 'user' ? 'justify-end' : 'justify-start'} items-center gap-2`}>
      {messagedAt && (
        <span className={`text-xs ${message?.role !== 'user' ? 'text-gray-500 dark:text-gray-400' : 'text-white opacity-60'}`}>
          {formatMessageTimestamp(messagedAt)}
        </span>
      )}
      {message.role !== 'user' && (
        <button
          className='cursor-pointer disabled:cursor-not-allowed border-none bg-transparent color-primary mb-0.5'
          onClick={(e) => handleMessageMenu(e, message, index)}
          disabled={message.is_flagged}
        >
          <svg width="16" height="6" viewBox="0 0 17 3" fill={'currentColor'} xmlns="http://www.w3.org/2000/svg">
            <circle cx="1.5" cy="1.5" r="1.5" />
            <circle cx="8.5" cy="1.5" r="1.5" />
            <circle cx="15.5" cy="1.5" r="1.5" />
          </svg>
        </button>
      )}
    </div>
  );

  const renderAttachment = (attachment) => (
    <div
      key={attachment.id}
      className={`relative flex ${attachment.isImage ? 'p-0' : 'min-w-[140px] p-2'} items-center gap-2 rounded-2xl border border-[#ececec] bg-[#f7f7fb] dark:border-white/10 dark:bg-[#1d1d21] ${attachment.url && !attachment.isImage ? 'cursor-pointer hover:bg-[#f0f0f5] dark:hover:bg-[#1a1a1e]' : ''}`}
      onClick={attachment.url && !attachment.isImage ? () => window.open(attachment.url, '_blank') : undefined}
    >
      <div
        className={`rounded-xl border border-[#e4e4eb] bg-white dark:border-white/10 dark:bg-[#141418] overflow-hidden flex items-center justify-center ${
          attachment.isImage ? 'h-[70px] w-[70px] cursor-pointer' : 'h-12 w-12'
        }`}
      >
        {attachment.isImage && (attachment.previewUrl || attachment.url) ? (
          <img 
            src={attachment.previewUrl || attachment.url} 
            alt={attachment.name} 
            className="h-full w-full object-cover"
            onClick={() => {
              setPreviewedImage({ 
                src: attachment.previewUrl || attachment.url, 
                alt: attachment.name || 'Image' 
              });
            }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full w-full">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
              />
              <path d="M14 2V8H20" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
            </svg>
          </div>
        )}
      </div>
      {!attachment.isImage && (
        <div className="flex flex-col min-w-0">
          <p className="mb-0 text-[13px] font-medium text-[#151521] dark:text-white truncate">{attachment.name}</p>
          <span className="text-[11px] text-gray-600 dark:text-gray-200">{attachment.extension}</span>
        </div>
      )}
    </div>
  );

  return (
    <div
      className="chat-message my-[10px] sm:my-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`flex items-start ${message.role === 'user' ? 'justify-end' : ''}`}>
        <div className={`flex flex-col max-w-[75%] sm:max-w-[70%] ${message.role === 'user' && 'mr-0'} mx-2 order-${message.role === 'user' ? '1' : 'last'} items-${message.role === 'user' ? 'end' : 'start rounded-xl rounded-bl-none'}`}>
          {hasAttachments && (
            <div className={`w-full flex flex-col gap-2 ${hasTextContent ? 'mb-2' : ''}`}>
              {/* Images displayed in a row */}
              {imageAttachments.length > 0 && (
                <div className={`w-full flex flex-row flex-wrap gap-2 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {imageAttachments.map((attachment) => renderAttachment(attachment))}
                </div>
              )}
              {/* Non-image files displayed one by one below images */}
              {nonImageAttachments.length > 0 && (
                <div className={`w-full flex flex-col gap-2 ${message.role === 'user' ? 'items-end' : 'items-start'}`}>
                  {nonImageAttachments.map((attachment) => renderAttachment(attachment))}
                </div>
              )}
            </div>
          )}
          {hasTextContent && (
            <div className={`${message.role === 'user' ? 'pt-2' : 'pt-3'} pb-2 px-[10px] sm:px-[15px] rounded-xl w-full ${message.role === 'user' && 'whitespace-pre-wrap'} break-words ${roundedClass} ${bgColor} ${textColor} ${message.role} ${message.is_flagged ? 'opacity-50' : ''}`} style={{ backgroundColor: message.role === 'user' ? primary : '#f5f5f5' }}>
              <ReactMarkdown
                components={MarkdownComponents}
                className={`markdown-text text-xs md:text-[14px] md:leading-5 text-${language == 'ar' ? 'right': 'left'}`}
                remarkPlugins={[remarkGfm]}
                skipHtml={true}
              >
                {sanitizeContent(message.content)}
              </ReactMarkdown>
              {renderMetaRow()}
            </div>
          )}
          {!hasTextContent && hasAttachments && (
            <div className="mt-2">
              {renderMetaRow()}
            </div>
          )}
          {message.role !== 'user' && (
            <FeedbackButtons />
          )}
          {message.is_flagged == '1' && <p className='text-[12px] leading-[12px] my-1 text-red-500'>{t('This message has been reported!')}</p>}
          {/* {message.role !== 'user' && (
            <div className={`flex flex-col py-2 w-full`}>
              <div className='flex justify-between items-center w-full'>
                <div className='flex ml-2'>
                  <p className='text-[12px] leading-[12px] mb-0'>Sources</p>
                  <div className='flex' onClick={handleSources}>
                    <img className={`ml-2 cursor-pointer ${openSources && 'rotate180 mr-2 ml-0'}`} src='/assets/images/dropdown-icon.svg' width={12} height={12} />
                  </div>
                </div>
                <div className='flex items-start self-stretch'>
                  <div className='cursor-pointer mx-2 flex' onClick={handleReportBtn}>
                    <img src='/assets/images/flag-icon.svg' width={18} height={18} alt='Flag' />
                  </div>
                  <div className='cursor-pointer mx-2 flex'>
                    <svg xmlns="http://www.w3.org/2000/svg" height="18" viewBox="0 -960 960 960" width="18" className={`fill-[${primaryColor}]`}>
                      <path d="M720-80q-50 0-85-35t-35-85q0-7 1-14.5t3-13.5L322-392q-17 15-38 23.5t-44 8.5q-50 0-85-35t-35-85q0-50 35-85t85-35q23 0 44 8.5t38 23.5l282-164q-2-6-3-13.5t-1-14.5q0-50 35-85t85-35q50 0 85 35t35 85q0 50-35 85t-85 35q-23 0-44-8.5T638-672L356-508q2 6 3 13.5t1 14.5q0 7-1 14.5t-3 13.5l282 164q17-15 38-23.5t44-8.5q50 0 85 35t35 85q0 50-35 85t-85 35Zm0-640q17 0 28.5-11.5T760-760q0-17-11.5-28.5T720-800q-17 0-28.5 11.5T680-760q0 17 11.5 28.5T720-720ZM240-440q17 0 28.5-11.5T280-480q0-17-11.5-28.5T240-520q-17 0-28.5 11.5T200-480q0 17 11.5 28.5T240-440Zm480 280q17 0 28.5-11.5T760-200q0-17-11.5-28.5T720-240q-17 0-28.5 11.5T680-200q0 17 11.5 28.5T720-160Zm0-600ZM240-480Zm480 280Z" />
                    </svg>
                  </div>
                </div>
              </div>
              {openSources && (<div className='mt-2 ml-2'>
                <p className='text-[12px] leading-[12px] underline'>FinancialReport2023</p>
              </div>)}
            </div>
          )} */}
        </div>
        {/* {message.role !== "user" && <img src={image} alt="My profile" className="w-7 h-7 rounded-full order-first" />} */}
      </div>
      
      <ImagePreview
        isOpen={!!previewedImage && !!previewedImage.src}
        imageSrc={previewedImage?.src}
        imageAlt={previewedImage?.alt || 'Image preview'}
        onClose={() => setPreviewedImage(null)}
      />
    </div>
  );
};

export default Message;
